<!--
  One row of the left sidebar. A built feature is a link. A feature that is
  not built yet is a button with a Soon tag that opens the coming-soon window.
  In the icon rail the label hides and a tooltip names the item.

  A feature in testing has a Beta tag, which shows at all times.

  A row with sub-items has a chevron. The label opens the page, the chevron
  shows or hides the sub-items without leaving the page. On one of its own
  pages a group is always open when the page loads.

  A row with no page is a button (the mentor beta's "Join the beta" opens
  its window).
-->
<template>
  <div class="gm-sb-row" :data-tour="`nav-${item.key}`">
    <button
      v-if="item.comingSoon"
      v-tooltip="tooltip"
      type="button"
      class="gm-sb-item is-soon"
      @click="openComingSoon(item.comingSoon)"
    >
      <component :is="item.icon" class="gm-sb-icon" aria-hidden="true" />
      <span class="gm-sb-label">{{ item.label }}</span>
      <span class="gm-sb-soon">Soon</span>
    </button>

    <button
      v-else-if="!item.href"
      v-tooltip="tooltip"
      type="button"
      class="gm-sb-item"
      :test_id="`sidebar-${item.key}`"
      @click="item.onClick?.()"
    >
      <component :is="item.icon" class="gm-sb-icon" aria-hidden="true" />
      <span class="gm-sb-label">{{ item.label }}</span>
    </button>

    <InertiaLink
      v-else
      v-tooltip="tooltip"
      :href="item.href"
      class="gm-sb-item"
      :class="{
        'is-active': isActive(item),
        'has-active-child': hasActiveChild,
        'has-chevron': hasChildren,
        'is-beta': item.beta,
      }"
      :aria-current="isActive(item) ? 'page' : undefined"
      prefetch="hover"
      @click="item.onClick?.()"
    >
      <component :is="item.icon" class="gm-sb-icon" aria-hidden="true" />
      <span class="gm-sb-label">{{ item.label }}</span>
      <span v-if="item.beta" class="gm-sb-beta">Beta</span>
      <span v-if="item.isLive" class="gm-sb-live-dot" aria-hidden="true" />
      <span
        v-if="item.hasDot"
        class="bg-primary-500 ml-auto size-2 shrink-0 rounded-full"
        role="img"
        aria-label="Needs you"
      />
      <span v-if="item.liveCount" class="gm-sb-live-count">
        <span class="sr-only">,</span>
        {{ item.liveCount }}
        <span class="sr-only">live</span>
      </span>
      <span v-if="item.badge" class="gm-sb-badge">
        <span class="sr-only">,</span>
        {{ item.badge > 99 ? '99+' : item.badge }}
        <span class="sr-only">new</span>
      </span>
    </InertiaLink>

    <button
      v-if="hasChildren"
      type="button"
      class="gm-sb-chevron"
      :aria-expanded="isOpen"
      :aria-controls="isOpen ? subsId : undefined"
      :aria-label="`More in ${item.label}`"
      @click="onChevronClick"
    >
      <ChevronDownIcon :class="{ 'is-open': isOpen }" aria-hidden="true" />
    </button>

    <div v-if="hasChildren && isOpen" :id="subsId" class="gm-sb-subs">
      <template v-for="child in item.children" :key="child.key">
        <button
          v-if="child.comingSoon"
          type="button"
          class="gm-sb-sub"
          @click="openComingSoon(child.comingSoon)"
        >
          <span v-if="child.isLive" class="gm-sb-live-dot" aria-hidden="true" />
          {{ child.label }}
          <span class="gm-sb-soon">Soon</span>
        </button>
        <InertiaLink
          v-else
          :href="child.href"
          class="gm-sb-sub"
          :data-tour="`nav-${child.key}`"
          :class="{ 'is-active': child.key === activeChildKey }"
          :aria-current="child.key === activeChildKey ? 'page' : undefined"
        >
          <span v-if="child.isLive" class="gm-sb-live-dot" aria-hidden="true" />
          {{ child.label }}
          <span v-if="child.badge" class="gm-sb-badge is-sub">
            <span class="sr-only">,</span>
            {{ child.badge > 99 ? '99+' : child.badge }}
            <span class="sr-only">new</span>
          </span>
        </InertiaLink>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue'
import { usePage } from '@inertiajs/vue3'
import ChevronDownIcon from '~icons/lucide/chevron-down'
import {
  isPathMatch,
  pathOf,
  type SidebarItem,
  type SidebarSubItem,
} from './useSidebarNavigation'
import { useSidebarState } from './useSidebarState'

const props = defineProps<{
  item: SidebarItem
  isActive: (item: {
    match?: string[]
    exact?: string[]
    href?: string
  }) => boolean
  isRail: boolean
}>()

const { openComingSoon, openGroups, toggleGroup } = useSidebarState()

const page = usePage()
const subsId = useId()
const hasChildren = computed(() => !!props.item.children?.length)
const isCurrentGroup = computed(() => props.isActive(props.item))
// One active child: the one whose matching prefix is the longest. City
// Squads needs it: every child matches /cities, and /cities/events must light
// Events alone.
// An exact path counts as a match as long as the path itself (Hiring's Board
// takes /hiring as it is, not the pages below it).
const matchLength = (child: SidebarSubItem) =>
  Math.max(
    0,
    ...(child.exact ?? [])
      .filter((path) => path === pathOf(page.url))
      .map((path) => path.length),
    ...(child.match ?? (child.href && !child.exact ? [child.href] : []))
      .filter((prefix) => isPathMatch(page.url, [prefix]))
      .map((prefix) => prefix.length),
  )
// A page can name its row in the `sidebarActive` prop, for a URL that does
// not tell the rows apart: a dinner and an event share /cities/:city/events.
// The named row wins when this group has it; a row the viewer does not have
// (Dinners with its flag off) falls back to the longest match.
const namedChildKey = computed(() => {
  const key = page.props.sidebarActive
  return (props.item.children ?? []).find(
    (child) => child.key === key && !child.comingSoon,
  )?.key
})
const activeChildKey = computed(
  () =>
    namedChildKey.value ??
    [...(props.item.children ?? [])]
      .filter((child) => !child.comingSoon && matchLength(child) > 0)
      .sort((a, b) => matchLength(b) - matchLength(a))[0]?.key,
)
const hasActiveChild = computed(
  () => isOpen.value && !props.isRail && !!activeChildKey.value,
)

// On its own pages the group is open. The chevron can close it for this page
// only, and the next page visit opens it again. Elsewhere the group is closed
// by default, and a group the member opens stays open until the page loads
// again.
const isClosedOnThisPage = ref(false)
watch(
  () => page.url,
  () => {
    isClosedOnThisPage.value = false
  },
)

const isOpen = computed(() =>
  isCurrentGroup.value
    ? !isClosedOnThisPage.value
    : !!openGroups.value[props.item.key],
)

const onChevronClick = () => {
  if (isCurrentGroup.value) {
    isClosedOnThisPage.value = !isClosedOnThisPage.value
    return
  }
  toggleGroup(props.item.key, isOpen.value)
}

const tooltip = computed(() => ({
  content: props.item.comingSoon
    ? `${props.item.label} (soon)`
    : props.item.liveCount
      ? `${props.item.label} (${props.item.liveCount})`
      : props.item.beta
        ? `${props.item.label} (beta)`
        : props.item.label,
  placement: 'right',
  disabled: !props.isRail,
}))
</script>
